// packages/ui/src/components/chat/PreChatForm.tsx
'use client';

import { useMemo, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useForm } from 'react-hook-form';
import type { TFunc } from '@tourtask/types/i18n';
import type { CountryWithName } from '@tourtask/types/configs/Country';
import { Form } from '@tourtask/ui/forms/Form';
// FormTextField and PhoneInput, drawn outlined-only: chat.js then carries no MUI TextField (outlinedFormFields.tsx).
import { OutlinedFormTextField, OutlinedPhoneInput } from './outlinedFormFields';
import type { CommonKey } from '@tourtask/i18n/keys';
import { preChatFormResolver, type PreChatFormValues } from './preChatFormResolver';

interface PreChatFormProps {
	formFields?: ReactNode;
  t: TFunc;
  countriesWithNames: CountryWithName[];
  starting: boolean;
  error: string | null;
  /** Intro line above the fields. Defaults to the chat "How can we help" prompt;
   *  the email-only widget passes its own ("leave a message"). */
  introKey?: CommonKey;
  /** Submit-button label key. Defaults to "Start chat"; the email-only widget
   *  passes "Send". */
  submitKey?: CommonKey;
  /** Max message length. Defaults to 4000 (the live-chat first-message cap); the
   *  email-only widget passes the lower /contact-requests cap so client-side
   *  validation matches the server (no opaque over-length rejection). */
  messageMaxLength?: number;
  /** A message the visitor already wrote (in a chat that asked for no contact details until the
   *  business turned the form back on): the form starts with it. */
  initialMessage?: string;
  onStart: (values: {
    name: string;
    email: string;
    /** ISO country code (CHAR(2)) + local number, kept separate — never combined
     *  with the calling code (the repo phone-storage convention). */
    phoneCountryCode: string;
    phoneNumber: string;
    firstMessage: string;
  }) => void;
}

/**
 * The chat pre-chat form of the embeddable business widget (embed): its live chat
 * and its email-only form (apps/public's support widget has none: it reuses the
 * composer only). The visitor must provide name + email + phone before sending the
 * first message. Built on the SAME react-hook-form primitives + shared `PhoneInput`
 * (country selector + number) as the storefront contact form, drawn outlined-only
 * (`outlinedFormFields.tsx`) — so the phone UX is identical to the rest of the
 * product. Pure: `t: TFunc` is injected (no next-intl), so it stays browser-safe
 * for the CRA embed bundle. The selected ISO country code + local number are
 * emitted SEPARATELY (the repo phone convention) — the calling code is resolved
 * for display later, never stored combined.
 */
export function PreChatForm({
  formFields,
  t,
  countriesWithNames,
  starting,
  error,
  introKey = 'How_can_we_help',
  submitKey = 'Start_chat',
  messageMaxLength = 4000,
  initialMessage = '',
  onStart,
}: PreChatFormProps) {
  // Error messages are translation keys, resolved by `Form` via `t` (the same
  // convention as InquiryDialog). The options are `useZodForm`'s.
  const resolver = useMemo(() => preChatFormResolver(messageMaxLength), [messageMaxLength]);
  const form = useForm<PreChatFormValues>({
    resolver,
    mode: 'onSubmit',
    criteriaMode: 'firstError',
    shouldFocusError: true,
    defaultValues: {
      name: '',
      email: '',
      // Phone country starts UNSELECTED: the visitor is typically a tourist, not
      // from the operator's country, so we never preselect the business country —
      // they pick their own (the field is still required before submit).
      phone: { countryCode: '', number: '' },
      message: initialMessage,
    },
  });

  const submit = (values: PreChatFormValues) => {
    onStart({
      name: values.name.trim(),
      email: values.email.trim(),
      phoneCountryCode: values.phone.countryCode,
      phoneNumber: values.phone.number.trim(),
      firstMessage: values.message.trim(),
    });
  };

  return (
    <Box sx={{ p: 2, overflowY: 'auto' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t(introKey)}
      </Typography>
      <Form form={form} onSubmit={submit} t={t}>
        {formFields}
        <Stack spacing={2}>
          <OutlinedFormTextField<PreChatFormValues> name="name" label={t('Your_name')} required fullWidth disabled={starting} />
          <OutlinedFormTextField<PreChatFormValues>
            name="email"
            label={t('Your_email')}
            type="email"
            required
            fullWidth
            disabled={starting}
          />
          <OutlinedPhoneInput<PreChatFormValues>
            countryCodeName="phone.countryCode"
            numberName="phone.number"
            countriesWithNames={countriesWithNames}
            countryLabel={t('Phone_country')}
            numberLabel={t('Your_phone')}
            required
            disabled={starting}
            // The chat widget panel is ~370px: force the stacked field layout (so
            // the country name, "+CC", and number all have room — the calling code
            // still shows once a country is picked) and drop the per-option calling
            // code in the dropdown (it overlaps the name there). leftIcon removed above.
            compact
          />
          <OutlinedFormTextField<PreChatFormValues>
            name="message"
            label={t('Your_message')}
            required
            fullWidth
            multiline
            rows={3}
            disabled={starting}
          />
          {error && (
            <Typography color="error" variant="body2">
              {t('Chat_could_not_be_started')}
            </Typography>
          )}
          <Button type="submit" variant="contained" loading={starting}>
            {t(submitKey)}
          </Button>
        </Stack>
      </Form>
    </Box>
  );
}
